// @ts-nocheck
import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from '@/i18n';

interface DialerEventPayload {
  name?: string;
  payload?: Record<string, unknown>;
  [key: string]: unknown;
}

interface UseDialerEventsArgs {
  agentEmail?: string | null;
  queryClient: QueryClient;
  clientEmail?: string | null;
  onEvent?: (name: string, payload: DialerEventPayload) => void;
}

/**
 * useDialerEvents — hook SSE des événements dialer (UNIQUE source, anciennement
 * 'useDialerEventsLegacy', renommé 2026-05-21). Une variante TS plus minimale
 * (useDialerEvents.ts) avait été ébauchée mais jamais branchée — supprimée
 * car morte. Ce hook reste la seule implémentation et invalide les bonnes
 * queryKeys du dialer (['parallel-calls', ...] / ['all-calls', ...]).
 *
 * S'abonne au flux SSE /api/dialer/events?agentEmail=... pour
 * recevoir en push (~50ms) les transitions critiques d'appels (voicemail detecte,
 * humain confirme, appel termine) au lieu d'attendre le poll React Query (500ms).
 *
 * Les queries React Query existantes restent actives comme filet de securite —
 * ce hook ne fait qu'invalidate a la reception d'un event, ce qui declenche un
 * refetch immediat.
 *
 * Onhandlers optionnels : le caller peut reagir specifiquement a un event
 * (ex. fermer une modale, jouer un son) sans attendre la prochaine invalidation.
 */
export function useDialerEvents({ agentEmail, queryClient, clientEmail, onEvent }: UseDialerEventsArgs) {
  // 2026-06-05 — Stable ref pour onEvent. Sinon le useEffect re-tire a chaque
  // render du provider (onEvent recree a chaque render = nouvelle reference),
  // ce qui fait close+reopen du EventSource toutes les 500ms -> bombarde
  // /sse/token -> 429 -> bandeau "Telephonie non connectee".
  const onEventRef = useRef(onEvent);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    if (!agentEmail) return;
    let es: EventSource | null = null;
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    // 2026-10-07: the stream reconnects by itself. The browser's own
    // EventSource retry reuses the URL, whose token lives 5 minutes, so after
    // a server restart it met a refusal and gave up for good: a caller's
    // contact card stopped opening for an hour. Now any error or silence
    // (no 'ping' for 50 s, the server sends one every 20 s) closes the stream
    // and opens a new one with a new token, after 1, 2, 4... up to 30 s.
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let watchdog: ReturnType<typeof setInterval> | null = null;
    let attempt = 0;
    let lastSeen = Date.now();
    let lastReport = 0;
    const report = (state: string, detail = '') => {
      // At most one report every 30 s, except 'open' after a failure.
      const now = Date.now();
      if (state !== 'open' && now - lastReport < 30_000) return;
      lastReport = now;
      fetch('/api/sse/status', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state, detail: String(detail).slice(0, 200) }),
      }).catch(() => undefined);
    };
    const reconnect = (why: string) => {
      if (cancelled || retryTimer) return;
      if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
      if (es) { try { es.close(); } catch { /* ignore */ } es = null; }
      const delay = Math.min(30_000, 1000 * 2 ** attempt);
      attempt += 1;
      console.warn('[SSE] dialer-events reconnect in', delay, 'ms after', why);
      report('reconnecting', `${why}, attempt ${attempt}, in ${delay} ms`);
      retryTimer = setTimeout(() => { retryTimer = null; openWithToken(); }, delay);
    };

    const openWithToken = async () => {
      // Fetch a short-lived SSE token (scoped to agentEmail) — EventSource
      // can't carry cookies reliably, so the token is the auth carrier.
      let token;
      try {
        const res = await fetch('/api/sse/token', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'dialer-events', agentEmail }),
        });
        if (!res.ok) {
          console.warn('[SSE] dialer-events token request failed status=', res.status);
          report('token_failed', `HTTP ${res.status}`);
          reconnect(`token HTTP ${res.status}`);
          return;
        }
        const data = await res.json();
        token = data.token;
      } catch (e) {
        console.warn('[SSE] dialer-events token fetch threw', e?.message);
        report('token_failed', e?.message || 'fetch threw');
        reconnect('token fetch threw');
        return;
      }
      if (cancelled || !token) return;

      const url = `/api/dialer/events?agentEmail=${encodeURIComponent(agentEmail)}&token=${encodeURIComponent(token)}`;
      try {
        es = new EventSource(url);
        console.log('[SSE] dialer-events EventSource opened agentEmail=', agentEmail);
      } catch (e) {
        console.error('[SSE] dialer-events EventSource constructor failed', e?.message);
        report('error', `constructor: ${e?.message || ''}`);
        reconnect('constructor failed');
        return;
      }
      lastSeen = Date.now();
      es.onopen = () => {
        lastSeen = Date.now();
        if (attempt > 0) report('open', `after ${attempt} attempt(s)`);
        attempt = 0;
      };
      es.addEventListener('ping', () => { lastSeen = Date.now(); });
      // DX-4: the server closed this stream because the user opened a sixth
      // one (another tab). Reconnecting would evict that tab in turn: stay
      // on the polling fallback and say why.
      es.addEventListener('evicted', () => {
        cancelled = true;
        if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
        if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
        if (watchdog) { clearInterval(watchdog); watchdog = null; }
        if (es) { try { es.close(); } catch { /* ignore */ } es = null; }
        lastReport = 0;
        report('evicted', 'SSE_EVICTED');
        toast.warning(i18n.t('dialer:sse.evicted'), { id: 'sse-evicted', duration: Infinity });
      });

      const invalidateCalls = () => {
        try {
          // Invalide TOUTES les queries qui commencent par ces prefixes —
          // l'ancienne version filtrait sur clientEmail mais la query reelle
          // a comme key ['parallel-calls', callerData?.id, selectedCustomerId,
          // callerData?.caller_email] (4 elements). Le prefix matching de
          // React Query refresh tout ce qui commence par 'parallel-calls'.
          queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
          queryClient.invalidateQueries({ queryKey: ['all-calls'] });
        } catch { /* ignore */ }
      };

      const handle = (name) => (evt) => {
        lastSeen = Date.now();
        let payload = {};
        try { payload = JSON.parse(evt.data || '{}'); } catch { /* ignore */ }
        console.log('[SSE]', name, payload);
        invalidateCalls();
        // The device context listens too: the standing conference opens the
        // contact card on call:bridged and ends the leg on call:completed.
        try { window.dispatchEvent(new CustomEvent('revcall:dialer-event', { detail: { name, payload } })); } catch { /* ignore */ }
        const cb = onEventRef.current;
        if (typeof cb === 'function') {
          try { cb(name, payload); } catch { /* ignore */ }
        }
      };

      es.addEventListener('call:initiated', handle('call:initiated'));
      es.addEventListener('call:ringing',   handle('call:ringing'));
      es.addEventListener('call:answered',  handle('call:answered'));
      es.addEventListener('call:voicemail', handle('call:voicemail'));
      es.addEventListener('call:completed', handle('call:completed'));
      es.addEventListener('call:bridged',   handle('call:bridged'));
      // SR2-9: a contact of the organisation changed (import, edit, delete):
      // re-read the queue now rather than at the next poll.
      es.addEventListener('queue:changed', () => {
        lastSeen = Date.now();
        try { queryClient.invalidateQueries({ queryKey: ['prospect-queue'] }); } catch { /* ignore */ }
      });

      es.onerror = (e) => {
        console.warn('[SSE] dialer-events error', e?.type, 'readyState=', es?.readyState);
        report('error', `readyState ${es?.readyState}`);
        reconnect('stream error');
      };

      // Refresh token 30s avant expiration (5 min TTL) — ferme l'ancien et rouvre.
      refreshTimer = setTimeout(() => {
        if (!cancelled && es) { try { es.close(); } catch { /* ignore */ } es = null; openWithToken(); }
      }, (5 * 60 - 30) * 1000);
    };

    openWithToken();
    watchdog = setInterval(() => {
      if (es && Date.now() - lastSeen > 50_000) { report('stale', 'no ping for 50 s'); reconnect('no ping for 50 s'); }
    }, 15_000);

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      if (retryTimer) clearTimeout(retryTimer);
      if (watchdog) clearInterval(watchdog);
      if (es) { try { es.close(); } catch { /* ignore */ } }
    };
    // onEvent volontairement EXCLU des deps - on le lit via onEventRef
    // pour eviter le close+reopen 25x/seconde du EventSource.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentEmail, clientEmail, queryClient]);
}
